import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';
import { IMinorDelayConfiguration } from '../types/ISettings';

import {
  MESSAGES,
  SCOPES,
  createNotificationHandler,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';

export const minorDelayApi = createApi({
  reducerPath: 'minorDelayApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['MinorDelays'],
  endpoints: (builder) => ({
    getMinorDelayConfigurations: builder.query<
      IMinorDelayConfiguration[],
      void
    >({
      query: () => ({
        url: 'Configurations/MinorDelays',
        method: 'get',
      }),
      providesTags: ['MinorDelays'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_MINOR_DELAY,
        MESSAGES.LOAD,
        false
      ),
    }),
    createMinorDelayConfiguration: builder.mutation<
      IMinorDelayConfiguration,
      Partial<IMinorDelayConfiguration>
    >({
      query: (configuration) => ({
        url: 'Configurations/MinorDelays',
        method: 'post',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.ADD,
          true
        )(configuration, { dispatch, queryFulfilled });

        const { data: newItem } = await queryFulfilled;
        dispatch(
          minorDelayApi.util.updateQueryData(
            'getMinorDelayConfigurations',
            undefined,
            (draft) => {
              draft.push(newItem);
            }
          )
        );
      },
    }),
    updateMinorDelayConfiguration: builder.mutation<
      IMinorDelayConfiguration,
      IMinorDelayConfiguration
    >({
      query: (configuration) => ({
        url: `Configurations/MinorDelays/${configuration.id}`,
        method: 'put',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.UPDATE,
          true
        )(configuration, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;
        dispatch(
          minorDelayApi.util.updateQueryData(
            'getMinorDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === updatedItem.id);
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], ...updatedItem };
              }
            }
          )
        );
      },
    }),
    removeMinorDelayConfiguration: builder.mutation<
      { success: boolean },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `Configurations/MinorDelays/${id}`,
        method: 'delete',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.DELETE,
          true
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;
        dispatch(
          minorDelayApi.util.updateQueryData(
            'getMinorDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetMinorDelayConfigurationsQuery,
  useCreateMinorDelayConfigurationMutation,
  useUpdateMinorDelayConfigurationMutation,
  useRemoveMinorDelayConfigurationMutation,
} = minorDelayApi;

export default minorDelayApi;
